/**
 * ComparisonStackedView Component
 *
 * @deprecated Use ComparisonView from '@/components/ComparisonView' instead.
 * This component is kept for backwards compatibility but will be removed in a future version.
 * The new ComparisonView handles responsiveness via CSS container queries.
 *
 * Mobile-optimized stacked card view for record comparison.
 * Shows one record at a time with swipe/tap to switch.
 * Optimized for touch interactions and smaller screens.
 */

import { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import { Typography, Switch, Space, Tag, Empty, Alert, Button, Segmented, Tooltip, message } from 'antd'
import {
  SwapOutlined,
  DiffOutlined,
  LeftOutlined,
  RightOutlined,
  CopyOutlined,
  CheckOutlined,
} from '@ant-design/icons'
import type { FieldMetadata, FormSection } from '@/services/metadataService'
import type { ComparisonConfig, FieldDifference } from './types'
import { useFieldComparison } from './useFieldComparison'
import {
  groupDifferencesBySection,
  getOrderedSections,
} from './ComparisonFieldSection'
import { formatCellValue, isLifecycleField, renderLifecycleStatus } from '../../utils/columnFormatters'
import { isJsonField, isJsonValue } from '../../utils/jsonFormatters'
import { JsonValueDisplay } from '../JsonValueDisplay'
import { formatComparisonAsText, copyTextToClipboard } from './utils/copyToClipboard'

const { Text } = Typography

/**
 * Format record display title.
 */
function formatRecordTitle(record: Record<string, unknown> | null): string {
  if (!record) return 'No record'
  const name = record.name || record.title || record.label || record.uid
  return String(name ?? 'Unnamed')
}

/**
 * Get lifecycle state color for tag.
 */
function getStateColor(state: string | undefined): string {
  if (!state) return 'default'
  const colorMap: Record<string, string> = {
    draft: 'processing',
    active: 'success',
    expired: 'default',
    pending: 'warning',
  }
  return colorMap[state.toLowerCase()] || 'default'
}

/**
 * Render a field value for mobile display.
 */
function renderMobileValue(
  value: unknown,
  fieldKey: string,
  metadata?: FieldMetadata
): React.ReactNode {
  if (value === null || value === undefined) {
    return <Text type="secondary" italic>-</Text>
  }

  if (metadata && isLifecycleField(fieldKey, metadata) && typeof value === 'string') {
    return renderLifecycleStatus(value)
  }

  // JSON fields - use interactive popover display
  if ((metadata && isJsonField(metadata)) || isJsonValue(value)) {
    return <JsonValueDisplay value={value} maxHeight={200} collapsed={1} />
  }

  if (metadata) {
    const formatted = formatCellValue(value, metadata)
    return <Text style={{ fontSize: 14 }}>{formatted}</Text>
  }

  return <Text style={{ fontSize: 14 }}>{String(value)}</Text>
}

/**
 * Format numeric delta for display.
 */
function formatDelta(delta: number, percent?: number): string {
  const prefix = delta > 0 ? '+' : ''
  const deltaStr = `${prefix}${delta.toFixed(2)}`
  if (percent !== undefined) {
    const percentPrefix = percent > 0 ? '+' : ''
    return `${deltaStr} (${percentPrefix}${percent.toFixed(1)}%)`
  }
  return deltaStr
}

interface MobileFieldCardProps {
  difference: FieldDifference
  metadata?: FieldMetadata
  isKeyField?: boolean
  activeView: 'current' | 'revised'
}

/**
 * Single field card for mobile stacked view.
 */
function MobileFieldCard({
  difference,
  metadata,
  isKeyField = false,
  activeView,
}: MobileFieldCardProps): JSX.Element {
  const { fieldKey, anchorValue, comparisonValue, isDifferent, numericDelta, percentDelta } = difference

  // Get field title
  const title = metadata?.title || fieldKey.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()

  // Get the value to display based on active view
  const displayValue = activeView === 'current' ? comparisonValue : anchorValue

  return (
    <div
      style={{
        padding: '12px 16px',
        backgroundColor: isDifferent ? 'var(--rp-temporal-orange-soft)' : 'var(--rp-surface-primary)',
        borderBottom: '1px solid var(--rp-border-subtle)',
        borderLeft: isKeyField ? '3px solid var(--rp-blue-primary)' : undefined,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <Text
            type="secondary"
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {title}
            {isKeyField && (
              <Tag color="blue" style={{ marginLeft: 6, fontSize: 9 }}>Key</Tag>
            )}
          </Text>
          <div style={{ marginTop: 4 }}>
            {renderMobileValue(displayValue, fieldKey, metadata)}
          </div>
        </div>

        {/* Show delta badge for numeric differences when viewing revised */}
        {isDifferent && numericDelta !== undefined && numericDelta !== 0 && activeView === 'revised' && (
          <Tag
            color={numericDelta > 0 ? 'orange' : 'green'}
            style={{ marginLeft: 8, fontSize: 11 }}
          >
            {formatDelta(numericDelta, percentDelta)}
          </Tag>
        )}

        {/* Show difference indicator */}
        {isDifferent && (
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: 'var(--rp-temporal-orange)',
              marginLeft: 8,
              flexShrink: 0,
            }}
            aria-label="Field has changed"
          />
        )}
      </div>
    </div>
  )
}

interface MobileSectionProps {
  sectionName: string
  differences: FieldDifference[]
  fields: Record<string, FieldMetadata>
  keyFields: Set<string>
  activeView: 'current' | 'revised'
}

/**
 * Section header and fields for mobile view.
 */
function MobileSection({
  sectionName,
  differences,
  fields,
  keyFields,
  activeView,
}: MobileSectionProps): JSX.Element {
  const title = sectionName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')

  const changedCount = differences.filter(d => d.isDifferent).length

  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 16px',
          backgroundColor: 'var(--rp-surface-tertiary)',
          borderRadius: '8px 8px 0 0',
        }}
      >
        <Text strong style={{ fontSize: 13 }}>{title}</Text>
        {changedCount > 0 && (
          <Tag color="orange" style={{ fontSize: 11 }}>{changedCount} changed</Tag>
        )}
      </div>
      <div style={{ backgroundColor: 'var(--rp-surface-primary)', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
        {differences.map(diff => (
          <MobileFieldCard
            key={diff.fieldKey}
            difference={diff}
            metadata={fields[diff.fieldKey]}
            isKeyField={keyFields.has(diff.fieldKey)}
            activeView={activeView}
          />
        ))}
      </div>
    </div>
  )
}

export interface ComparisonStackedViewProps {
  /** Anchor record (current - what we're comparing from) */
  anchorRecord: Record<string, unknown> | null
  /** Comparison record (baseline - what we're comparing against) */
  comparisonRecord: Record<string, unknown> | null
  /** Field metadata for formatting */
  fields: Record<string, FieldMetadata>
  /** Form sections for grouping */
  formSections: FormSection[]
  /** Comparison configuration */
  comparison?: ComparisonConfig | null
  /** Show only fields with differences */
  showDifferencesOnly: boolean
  /** Callback to toggle differences only mode */
  onToggleDifferencesOnly: () => void
}

/**
 * Mobile-optimized stacked view for comparing two records.
 * Shows one record at a time with tabs to switch between them.
 *
 * @example
 * ```tsx
 * <ComparisonStackedView
 *   anchorRecord={currentRecord}
 *   comparisonRecord={selectedComparison}
 *   fields={metadata.fields}
 *   formSections={metadata.formSections}
 *   comparison={metadata.comparison}
 *   showDifferencesOnly={preferences.showDifferencesOnly}
 *   onToggleDifferencesOnly={toggleShowDifferencesOnly}
 * />
 * ```
 */
export function ComparisonStackedView({
  anchorRecord,
  comparisonRecord,
  fields,
  formSections,
  comparison,
  showDifferencesOnly,
  onToggleDifferencesOnly,
}: ComparisonStackedViewProps): JSX.Element {
  // Track which view is active (current or revised)
  const [activeView, setActiveView] = useState<'current' | 'revised'>('revised')

  // Copy state
  const [isCopied, setIsCopied] = useState(false)

  // Touch handling for swipe
  const touchStartX = useRef<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Screen reader announcement state
  const [announcement, setAnnouncement] = useState('')

  // Calculate field differences
  const {
    differences,
    changedCount,
    hasDifferences,
    keyFieldChanges,
    hasComparison,
  } = useFieldComparison({
    anchorRecord,
    comparisonRecord,
    fields,
    comparison,
    showDifferencesOnly,
  })

  // Force switch to revised tab when comparison is cleared
  useEffect(() => {
    if (!hasComparison && activeView === 'current') {
      setActiveView('revised')
    }
  }, [hasComparison, activeView])

  // Key fields set for highlighting
  const keyFields = useMemo(() => new Set(comparison?.keyFields ?? []), [comparison?.keyFields])

  // Group differences by section
  const sectionGroups = useMemo(
    () => groupDifferencesBySection(differences, fields, formSections),
    [differences, fields, formSections]
  )
  const orderedSections = useMemo(
    () => getOrderedSections(formSections, sectionGroups),
    [formSections, sectionGroups]
  )

  // Announce view changes and comparison results to screen readers
  useEffect(() => {
    const viewLabel = activeView === 'current' ? 'Current' : 'Revised'
    if (hasComparison) {
      const msg = hasDifferences
        ? `Viewing ${viewLabel} record. ${changedCount} field${changedCount !== 1 ? 's' : ''} changed.`
        : `Viewing ${viewLabel} record. No differences found.`
      setAnnouncement(msg)
    } else {
      setAnnouncement(`Viewing ${viewLabel} record. No comparison record selected.`)
    }
  }, [hasComparison, activeView, hasDifferences, changedCount])

  // Handle swipe gestures
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
  }, [])

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (touchStartX.current === null) return

    const touchEndX = e.changedTouches[0].clientX
    const deltaX = touchEndX - touchStartX.current
    const threshold = 50 // Minimum swipe distance

    if (Math.abs(deltaX) > threshold) {
      if (deltaX > 0 && activeView === 'revised' && hasComparison) {
        // Swipe right - go to current (only if comparison exists)
        setActiveView('current')
      } else if (deltaX < 0 && activeView === 'current') {
        // Swipe left - go to revised
        setActiveView('revised')
      }
    }

    touchStartX.current = null
  }, [activeView, hasComparison])

  // Handle copy to clipboard
  const handleCopy = useCallback(async () => {
    try {
      const text = formatComparisonAsText(
        anchorRecord,
        comparisonRecord,
        differences,
        fields,
        formSections
      )
      await copyTextToClipboard(text)
      setIsCopied(true)
      message.success('Copied to clipboard')

      // Reset icon after 2 seconds
      setTimeout(() => setIsCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
      message.error('Failed to copy')
    }
  }, [anchorRecord, comparisonRecord, differences, fields, formSections])

  // Get record info for current view
  const activeRecord = activeView === 'current' ? comparisonRecord : anchorRecord
  const activeState = activeRecord?.lifecycleState as string | undefined
  const activeVersion = activeRecord?.lifecycleVersion

  return (
    <div
      ref={containerRef}
      className="comparison-stacked-view"
      role="region"
      aria-label="Record comparison"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{ touchAction: 'pan-y' }}
    >
      {/* Screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {announcement}
      </div>

      {/* View Toggle */}
      <div style={{ marginBottom: 16 }}>
        <Segmented
          block
          value={activeView}
          onChange={(value) => setActiveView(value as 'current' | 'revised')}
          options={[
            {
              value: 'current',
              disabled: !hasComparison,
              label: (
                <Space>
                  <LeftOutlined />
                  <span>Current</span>
                </Space>
              ),
            },
            {
              value: 'revised',
              label: (
                <Space>
                  <span>Revised</span>
                  <RightOutlined />
                </Space>
              ),
            },
          ]}
        />
      </div>

      {/* Record Header Card */}
      <div
        style={{
          padding: '12px 16px',
          backgroundColor: activeView === 'revised' ? 'var(--rp-temporal-blue-soft)' : 'var(--rp-success-soft)',
          borderRadius: 8,
          marginBottom: 16,
        }}
      >
        <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
          {activeView === 'current' ? 'Current (Comparing Against)' : 'Revised Record'}
        </Text>
        <div style={{ marginTop: 4 }}>
          {activeView === 'current' && !hasComparison ? (
            <Text type="secondary" italic>
              Select a record to compare
            </Text>
          ) : (
            <Space wrap>
              <Text strong style={{ fontSize: 16 }}>
                {formatRecordTitle(activeRecord)}
              </Text>
              {activeState && (
                <Tag color={getStateColor(activeState)}>{activeState}</Tag>
              )}
              {activeVersion != null && (
                <Tag color="blue" style={{ fontSize: 11 }}>v{String(activeVersion)}</Tag>
              )}
            </Space>
          )}
        </div>
      </div>

      {/* Summary and Toggle */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          padding: '0 4px',
        }}
      >
        <Space>
          <DiffOutlined style={{ color: hasComparison && hasDifferences ? 'var(--rp-temporal-orange)' : hasComparison ? 'var(--rp-success)' : 'var(--rp-gray-500)' }} />
          {!hasComparison ? (
            <Text type="secondary" style={{ fontSize: 13 }}>
              {differences.length} field{differences.length !== 1 ? 's' : ''} to compare
            </Text>
          ) : hasDifferences ? (
            <Text style={{ fontSize: 13 }}>
              <Text strong style={{ color: 'var(--rp-temporal-orange)' }}>{changedCount}</Text> changed
            </Text>
          ) : (
            <Text type="success" style={{ fontSize: 13 }}>No differences</Text>
          )}
        </Space>

        <Space size="small">
          <Text type="secondary" style={{ fontSize: 12 }}>Diff only</Text>
          <Switch
            size="small"
            checked={showDifferencesOnly}
            onChange={onToggleDifferencesOnly}
            disabled={!hasComparison}
          />
          <Tooltip title="Copy">
            <Button
              type="text"
              size="small"
              icon={isCopied ? <CheckOutlined style={{ color: 'var(--rp-success)' }} /> : <CopyOutlined />}
              onClick={handleCopy}
              aria-label="Copy comparison to clipboard"
              disabled={!hasComparison}
            />
          </Tooltip>
        </Space>
      </div>

      {/* Key field changes alert */}
      {keyFieldChanges.length > 0 && activeView === 'revised' && hasComparison && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <Text style={{ fontSize: 13 }}>
              <Text strong>{keyFieldChanges.length}</Text> key field
              {keyFieldChanges.length !== 1 ? 's' : ''} changed
            </Text>
          }
        />
      )}

      {/* Swipe hint */}
      {hasComparison && (
        <div
          style={{
            textAlign: 'center',
            marginBottom: 12,
            padding: '4px 0',
          }}
        >
          <Text type="secondary" style={{ fontSize: 11 }}>
            <SwapOutlined style={{ marginRight: 4 }} />
            Swipe left/right to switch views
          </Text>
        </div>
      )}

      {/* Field Sections */}
      {orderedSections.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Empty
            description={
              showDifferencesOnly
                ? 'No differences found'
                : 'No comparable fields'
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ) : (
        <div className="comparison-mobile-sections">
          {orderedSections.map(section => {
            const sectionDiffs = sectionGroups.get(section.name) || []
            return (
              <MobileSection
                key={section.name}
                sectionName={section.name}
                differences={sectionDiffs}
                fields={fields}
                keyFields={keyFields}
                activeView={activeView}
              />
            )
          })}
        </div>
      )}

      {/* Navigation hint at bottom */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 8px',
          borderTop: '1px solid var(--rp-border-subtle)',
          marginTop: 16,
        }}
      >
        <Button
          type="text"
          icon={<LeftOutlined />}
          disabled={activeView === 'current' || !hasComparison}
          onClick={() => setActiveView('current')}
        >
          Current
        </Button>
        <Button
          type="text"
          disabled={activeView === 'revised'}
          onClick={() => setActiveView('revised')}
        >
          Revised
          <RightOutlined style={{ marginLeft: 4 }} />
        </Button>
      </div>
    </div>
  )
}

export default ComparisonStackedView
